import { getStaticSlug } from './language';

import i18n from 'i18n';
import { MenuItemDataTypes } from 'services/menus/types';

// Recursive Menu
const groupMenus = (menus?: MenuItemDataTypes[]) => {
  if (!menus) {
    return [];
  }
  const recursiveMenus = (
    menuList: MenuItemDataTypes[],
    parentId: number,
  ): MenuItemDataTypes[] => {
    const menusGrouped: MenuItemDataTypes[] = [];
    menuList.forEach((menu) => {
      if (menu.parentId === parentId) {
        const subMenus = recursiveMenus(menuList, menu.id);
        menusGrouped.push(
          subMenus.length > 0
            ? {
              ...menu,
              subMenu: subMenus,
            }
            : {
              ...menu,
            },
        );
      }
    });
    return menusGrouped;
  };
  if (menus.length > 0) {
    const firstLevelParentId = menus.find((menu) => menu.depth === 1)!.parentId;
    return recursiveMenus(menus, firstLevelParentId);
  }
  return [];
};

export const returnRouteMenu = (menu: MenuItemDataTypes) => {
  if (menu.type === 'custom_link' && menu.link) {
    return menu.link;
  }
  if (menu.reference?.slug) {
    return `/${menu.reference?.slug}`;
  }
  return '#';
};

export default groupMenus;

export const prefixGroupMenus = (
  prefix: string,
  subPrefix?: string,
  menus?: MenuItemDataTypes[],
) => {
  if (!menus) {
    return [];
  }

  return menus.map((ele) => ({
    ...ele,
    reference: ele.reference
      ? {
        slug: `${prefix}/${ele.reference?.slug || ''}`,
      }
      : undefined,
    subMenu: ele.subMenu
      ? ele.subMenu.map((subEle) => ({
        ...subEle,
        reference: subEle.reference
          ? {
            slug: `${prefix}/${subPrefix}/${subEle.reference?.slug || ''}`,
          }
          : undefined,
      }))
      : undefined,
  })) as MenuItemDataTypes[];
};

// Header Menu Convert
const checkLinkOrSlug = (slug?: string, link?: string) => {
  if (link) {
    return link;
  }
  if (slug) {
    return `/${slug}`;
  }
  return undefined;
};

const returnPrefix = (slug?: string) => {
  if (slug) {
    return slug;
  }
  return undefined;
};

const returnRefSlug = (slug?: string) => {
  if (slug) {
    return { slug };
  }
  return undefined;
};

const checkComplexMenu = (
  menu: MenuItemDataTypes,
  subLink: string,
  preMenu?: MenuItemDataTypes,
) => {
  if (
    menu.type === 'custom_link'
    && (preMenu?.cssClass === 'menu-oc' || preMenu?.cssClass === 'menu-news')
  ) {
    return {
      prefix: returnPrefix(subLink),
    };
  }
  return {
    prefix: returnPrefix(preMenu?.prefix),
    reference: returnRefSlug(subLink),
  };
};

const checkCustomLinkType = (cssClass?: string, link?: string) => {
  switch (cssClass) {
    case 'menu-about':
      return 've-vcbs';
    case 'menu-ic':
      return 'khach-hang-ca-nhan';
    case 'menu-oc':
      return 'dich-vu-khtc';
    case 'menu-oc-oc':
      return 'khach-hang-to-chuc';
    case 'menu-oc-ib':
      return 'khach-hang-to-chuc/ngan-hang-dau-tu';
    case 'menu-ts':
      return 'ho-tro-ca-nhan';
    case 'menu-news':
      return 'tin-tuc';
    case 'menu-news-announce':
      return 'tin-tuc/cong-bo-thong-tin';
    case 'menu-news-report':
      return 'tin-tuc/bao-cao-vcbs';
    case 'menu-news-sgdck':
      return 'tin-tuc/tin-tuc-sgdck';
    default:
      return link;
  }
};

const checkPrefix = (prefix?: string) => {
  if (prefix) {
    return returnPrefix(prefix);
  }
  return undefined;
};

const checkMenuTypeToLink = (
  menu: MenuItemDataTypes,
  preMenu?: MenuItemDataTypes,
) => {
  let slug: string | undefined = checkLinkOrSlug(
    menu.reference?.slug,
    menu.link,
  );
  let prefix: string | undefined;
  let detail: string | undefined;

  switch (menu.type) {
    case 'custom_link':
      slug = checkCustomLinkType(menu.cssClass, menu.link) || '';
      break;
    case 'OneContent\\AboutUs\\Models\\Department':
      prefix = checkPrefix(preMenu?.prefix);
      detail = '/bo-may-quan-ly';
      break;
    // KHCN
    case 'OneContent\\KHCN\\Models\\ServiceCategory':
    case 'OneContent\\KHCN\\Models\\Service':
      prefix = checkPrefix(preMenu?.prefix);
      if (menu.depth === 3) {
        detail = '/chi-tiet-khach-hang-ca-nhan';
      }
      break;
    // HTGD
    case 'OneContent\\HTGD\\Models\\Fee':
      prefix = checkPrefix(preMenu?.prefix);
      if (menu.depth === 3) {
        detail = '/chi-tiet-ho-tro-ca-nhan';
      }
      break;
    // KHTC
    case 'OneContent\\NHDT\\Models\\Service':
    case 'OneContent\\KHTC\\Models\\Service':
    default:
      prefix = checkPrefix(preMenu?.prefix);
      if (menu.cssClass === 'menu-about') {
        prefix = checkCustomLinkType(menu.cssClass, menu.link);
        slug = '';
      }
      break;
  }

  return [prefix, detail, slug].join('');
};

const mapSubMenu = (
  menu?: MenuItemDataTypes[],
  preMenu?: MenuItemDataTypes,
) => {
  if (menu) {
    return menu.map((subMenu) => {
      const subLink = checkMenuTypeToLink(subMenu, preMenu);
      const sub = {
        ...subMenu,
        ...checkComplexMenu(subMenu, subLink, preMenu),
      };
      return {
        ...sub,
        subMenu: subMenu.subMenu
          ? subMenu.subMenu.map((subEle) => {
            const subEleLink = checkMenuTypeToLink(subEle, sub);
            return {
              ...subEle,
              prefix: returnPrefix(sub?.prefix),
              reference: returnRefSlug(subEleLink),
            };
          })
          : undefined,
      };
    });
  }
  return undefined;
};

export const headerPrefixGroupMenu = (menus?: MenuItemDataTypes[]) => {
  if (!menus) {
    return [];
  }

  return menus.map((ele) => {
    const link = checkMenuTypeToLink(ele);
    const menu = {
      ...ele,
      prefix: returnPrefix(link),
    };
    return {
      ...menu,
      subMenu: mapSubMenu(ele.subMenu, menu),
    };
  }) as MenuItemDataTypes[];
};
// Ends Header Menu Convert

const checkPrefixMenuFooterCopyright = (menu: MenuItemDataTypes) => {
  let slug: string | undefined;
  if (menu.link) {
    slug = menu.link;
  }

  if (menu.reference?.slug) {
    slug = `${menu.reference?.slug}`;
  }

  let prefix;
  let detail;

  if (menu.type === 'OneContent\\News\\Models\\Disclosure') {
    detail = getStaticSlug('NEWS_DISCLOSURE_DETAIL', i18n.language);
  } else {
    prefix = detectTypePrefix(menu.type, i18n.language)
      ? `/${detectTypePrefix(menu.type, i18n.language)}`
      : '';
  }

  return [prefix, detail, slug].join('');
};

export const prefixMenuFooterCopyright = (menus?: MenuItemDataTypes[]) => {
  if (!menus) {
    return [];
  }

  return menus.map((ele) => ({
    ...ele,
    reference: ele.reference
      ? {
        slug: `${getLanguagePrefix(i18n.language)}${checkPrefixMenuFooterCopyright(ele) || ''}`,
      }
      : undefined,
  })) as MenuItemDataTypes[];
};
// Ends Header Menu Convert

// News Menu Convert
const checkNewsTypePrefix = (cssClass?: string, link?: string) => {
  if (link) return link;
  switch (cssClass) {
    case 'menu-news-announce':
      return 'tin-tuc/cong-bo-thong-tin';
    case 'menu-news-report':
      return 'tin-tuc/bao-cao-vcbs';
    case 'menu-news-sgdck':
      return 'tin-tuc/tin-tuc-sgdck';
    default:
      return 'tin-tuc';
  }
};

export const newsPrefixGroupMenu = (menus?: MenuItemDataTypes[]) => {
  if (!menus) {
    return [];
  }

  return menus.map((ele) => {
    const prefix = checkNewsTypePrefix(ele.cssClass, ele.link);

    return {
      ...ele,
      reference: ele.reference
        ? {
          slug: `${prefix}/${ele.reference?.slug || ''}`,
        }
        : undefined,
      subMenu: ele.subMenu
        ? ele.subMenu.map((subEle) => ({
          ...subEle,
          reference: subEle.reference
            ? {
              slug: `${prefix}/${subEle.reference?.slug || ''}`,
            }
            : undefined,
        }))
        : undefined,
    };
  }) as MenuItemDataTypes[];
};
// Ends News Menu Convert

export const checkTypePrefixSlug = (type?: string, prefix?: string, slug?: string) => {
  if (!type || !slug) return '';
  if (type === 'pages') return `${slug}`;
  return `${prefix}/${slug}`;
};

// Detect Type Prefix
export const detectTypePrefix = (type: string, lang: string) => {
  switch (type) {
    // About
    case 'OneContent\\AboutUs\\Models\\Department':
      return getStaticSlug('ABOUT_DEPARTMENT', lang);
    // Ic
    case 'OneContent\\KHCN\\Models\\ServiceCategory':
      return getStaticSlug('INDIVIDUAL_CUSTOMER', lang);
    case 'OneContent\\KHCN\\Models\\Service':
      return getStaticSlug('INDIVIDUAL_CUSTOMER_DETAIL', lang);
    // Oc & Ib
    case 'OneContent\\NHDT\\Models\\Service':
      return getStaticSlug('BANK_INVESTMENT', lang);
    case 'OneContent\\KHTC\\Models\\Service':
      return getStaticSlug('ORGANIZE_CUSTOMER', lang);
    // Ts
    case 'OneContent\\HTGD\\Models\\Fee':
      return getStaticSlug('TRANSACTION_FEE', lang);
    // News
    case 'OneContent\\News\\Models\\DisclosureCategory':
      return getStaticSlug('NEWS_DISCLOSURE', lang);
    case 'OneContent\\News\\Models\\SGDCKNewsCategory':
      return getStaticSlug('NEWS_SGDCK', lang);
    // Pages
    default:
      return '';
  }
};

export const getLanguagePrefix = (lang: string) => {
  if (lang && lang !== 'vi') return `${lang}/`;
  return '';
};

export const typePrefixGroupMenu = (menus?: MenuItemDataTypes[]) => {
  if (!menus) {
    return [];
  }

  return menus.map((ele) => {
    const prefix = detectTypePrefix(ele.type, i18n.language);

    if (prefix) {
      return {
        ...ele,
        reference: ele.reference && ele.reference.slug
          ? {
            slug: `${getLanguagePrefix(i18n.language)}${prefix}/${ele.reference.slug}`,
          }
          : undefined,
      };
    }
    return {
      ...ele,
      reference: ele.reference && ele.reference.slug
        ? {
          slug: `${getLanguagePrefix(i18n.language)}${ele.reference.slug}`,
        }
        : undefined,
    };
  }) as MenuItemDataTypes[];
};
// End Detect Type Prefix

// Detect prefix slug of search results
export const detectTypePrefixSearchResult = (type: string) => {
  const lang = i18n.language;
  switch (type) {
    // Ic
    case 'individual_customer_services':
      return getStaticSlug('INDIVIDUAL_CUSTOMER_DETAIL', lang);
    // Ts
    case 'htgd_instructs':
      return getStaticSlug('TRANSACTION_SUPPORT_DETAIL', lang);
    case 'htgd_fees':
      return getStaticSlug('TRANSACTION_FEE', lang);
    // News
    case 'news':
      return getStaticSlug('NEWS_VCBS_DETAIL', lang);
    case 'sgdck_news':
      return getStaticSlug('NEWS_SGDCK_DETAIL', lang);
    case 'disclosures':
      return getStaticSlug('NEWS_DISCLOSURE_DETAIL', lang);
    case 'khtc_services':
      return getStaticSlug('ORGANIZE_CUSTOMER', lang);
    case 'nhdt_services':
      return getStaticSlug('BANK_INVESTMENT', lang);
    case 'nhdt_business':
      return getStaticSlug('BANK_INVESTMENT_DETAIL', lang);
    // Pages
    default:
      return '';
  }
};

export const getOgDataPage = (pageData: PageDataTypes) => ({
  ogDescription: pageData.ogDescription,
  ogImage: pageData.ogImage,
  ogTitle: pageData.ogTitle,
  ogType: pageData.ogType,
});

export const checkActiveMenu = (
  slug: string,
  setActive: (id: number)=>void,
  setSubActive: (id: number)=>void,
  menu?: MenuItemDataTypes[],
  preSlug?: string,
) => {
  const checkSlug = preSlug || slug;
  setActive(-1);
  menu?.every((e) => {
    if (e.subMenu?.length) {
      const menuItem = e.subMenu.find(
        (v) => v.reference?.slug && v.reference.slug.includes(checkSlug),
      );

      if (menuItem) {
        setActive(e.id);
        if (preSlug) setSubActive(menuItem.id);
        return false;
      }

      if (e.reference?.slug && e.reference?.slug.includes(checkSlug)) {
        setActive(e.id);
        return false;
      }

      return true;
    }

    if (e.reference?.slug && e.reference?.slug.includes(checkSlug)) {
      setActive(e.id);
      return false;
    }
    return true;
  });
};
